import React, { useContext, useEffect, useState } from "react";
import { Box, Heading, SimpleGrid, Card, CardHeader, CardBody, Table, Thead, Tbody, Tr, Th, Td, Spinner, Button, Flex, Badge, useToast } from "@chakra-ui/react";
import { Navigate } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import urlToId from "../../../helpers/UrlToId";

export default function AdminPlaces() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(undefined);
  const [reloading, setReloading] = useState(false);
  const [cityDistricts, setCityDistricts] = useState([]);
  const [filtering, setFiltering] = useState(false);
  const toast = useToast();

  // Hooks must be called unconditionally; avoid early return before effects

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, ciRes, dRes] = await Promise.all([
        client.get("/public/countries/"),
        client.get("/public/cities/"),
        client.get("/public/districts/"),
      ]);
      setCountries(cRes.data.results || cRes.data);
      setCities(ciRes.data.results || ciRes.data);
      setDistricts(dRes.data.results || dRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperUser) return;
    fetchAll();
  }, [isSuperUser]);

  useEffect(() => {
    const id = selectedCityId;
    if (!id || !isSuperUser) {
      setCityDistricts([]);
      return;
    }
    const fetchCityDistricts = async () => {
      setFiltering(true);
      try {
        const res = await client.get(`/public/districts/?city=${id}`);
        const data = res.data.results || res.data;
        setCityDistricts(Array.isArray(data) ? data : []);
      } finally {
        setFiltering(false);
      }
    };
    fetchCityDistricts();
  }, [selectedCityId, isSuperUser]);

  const handleReload = async () => {
    setReloading(true);
    try {
      await client.post("/admin/places/reload/", { skip_flush: true });
      toast({ title: "Veriler yenilendi", status: "success", duration: 3000 });
      await fetchAll();
    } catch (e) {
      toast({ title: "Yenileme hatası", description: e?.message || String(e), status: "error", duration: 4000 });
    } finally {
      setReloading(false);
    }
  };

  const visibleDistricts = selectedCityId ? cityDistricts : districts;
  const selectedCity = selectedCityId
    ? cities.find((ci) => urlToId(ci.url) === selectedCityId)
    : undefined;

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={6}>
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">Yerleşimler</Heading>
        <Button size="sm" colorScheme="blue" onClick={handleReload} isLoading={reloading} loadingText="Yenileniyor">
          Verileri Yeniden Yükle
        </Button>
      </Flex>
      {loading ? (
        <Spinner />
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Card variant="outline">
            <CardHeader>Ülkeler</CardHeader>
            <CardBody>
              <Table size="sm">
                <Thead>
                  <Tr><Th>Ad</Th></Tr>
                </Thead>
                <Tbody>
                  {countries.map((c) => (
                    <Tr key={c.url || c.name}><Td>{c.name}</Td></Tr>
                  ))}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
          <Card variant="outline">
            <CardHeader>Şehirler</CardHeader>
            <CardBody>
              <Table size="sm">
                <Thead>
                  <Tr><Th>Ad</Th></Tr>
                </Thead>
                <Tbody>
                  {cities.map((ci) => {
                    const id = urlToId(ci.url);
                    const isSelected = selectedCityId === id;
                    return (
                      <Tr
                        key={ci.url || ci.name}
                        cursor="pointer"
                        bg={isSelected ? "gray.100" : undefined}
                        _hover={{ bg: "gray.50" }}
                        onClick={() => setSelectedCityId(id)}
                      >
                        <Td>
                          {ci.name}
                          {isSelected && (
                            <Badge ml={2} colorScheme="green" variant="subtle">Seçili</Badge>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
          <Card variant="outline">
            <CardHeader>
              İlçeler {selectedCity && (
                <Badge ml={2} colorScheme="blue">{selectedCity.name}</Badge>
              )}
              {filtering && (
                <Badge ml={2} colorScheme="purple">Yükleniyor</Badge>
              )}
            </CardHeader>
            <CardBody>
              <Table size="sm">
                <Thead>
                  <Tr><Th>Ad</Th></Tr>
                </Thead>
                <Tbody>
                  {visibleDistricts.map((d) => (
                    <Tr key={d.url || d.name}><Td>{d.name}</Td></Tr>
                  ))}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}
    </Box>
  );
}